import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import JobCard from '../../components/JobCard';
import { API_URL } from '../../lib/utils';
import { 
  ArrowRight, 
  Search, 
  MapPin, 
  Briefcase,
  CheckCircle2,
  Users,
  Building2,
  GraduationCap,
  Heart,
  Bell,
  Mail,
  FileText,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const JobCategoryPage = ({ 
  category,
  title,
  metaTitle,
  metaDescription,
  heroTitle,
  heroSubtitle,
  searchKeywords,
  contentSections,
  relatedCategories,
  categoryFilter,
  iconComponent: IconComponent = Briefcase
}) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCategoryJobs();
    
    // Set SEO meta tags
    document.title = metaTitle || `${title} | INeedASocialWorker.com`;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaDescription || `Find ${title.toLowerCase()} opportunities across the United States. Browse openings at hospitals, hospices, schools, and community organizations. Apply with SW Express Apply™.`;

    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://ineedasocialworker.com/${category}`;

    // Add structured data for job listing page
    const listingSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": title,
      "description": metaDescription,
      "url": `https://ineedasocialworker.com/${category}`,
      "numberOfItems": totalJobs,
      "itemListElement": jobs.slice(0, 10).map((job, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://ineedasocialworker.com/jobs/${job.job_id}`
      }))
    };

    let schemaScript = document.getElementById('category-schema');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'category-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(listingSchema);

    return () => {
      const existingSchema = document.getElementById('category-schema');
      if (existingSchema) existingSchema.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, totalJobs]);

  const fetchCategoryJobs = async () => {
    setLoading(true);
    try {
      // Build query: prefer explicit category filter, else keyword search
      const url = categoryFilter
        ? `${API_URL}/jobs?category=${encodeURIComponent(categoryFilter)}&limit=12`
        : `${API_URL}/jobs?search=${encodeURIComponent(searchKeywords.join(' OR '))}&limit=12`;
      const response = await axios.get(url);
      setJobs(response.data.jobs);
      setTotalJobs(response.data.total);
    } catch (error) {
      console.error('Error fetching category jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrioritySignup = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/priority-signup`, {
        email,
        category: category,
        specialty: title
      });
      toast.success('You\'re on the priority list! We\'ll notify you when jobs are posted.');
      setEmail('');
    } catch (error) {
      // Still show success even if endpoint doesn't exist yet
      toast.success('You\'re on the priority list! We\'ll notify you when jobs are posted.');
      setEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  // Coming Soon component for when no jobs are available
  const ComingSoonSection = () => (
    <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 md:p-12 text-center border border-primary/10">
      <div className="w-16 h-16 rounded-2xl bg-secondary/20 flex items-center justify-center mx-auto mb-6">
        <Bell className="w-8 h-8 text-secondary" />
      </div>
      <h3 className="font-serif text-2xl md:text-3xl font-bold text-gray-900 mb-4">
        {title.replace('Jobs', 'Opportunities')} Are Coming Soon
      </h3>
      <p className="text-gray-600 text-lg mb-6 max-w-2xl mx-auto">
        We are currently vetting {category.replace(/-/g, ' ').replace('jobs', '')} employers in Texas and nationwide. 
        Join our priority candidate list to be notified when positions go live.
      </p>
      
      {/* Priority Signup Form */}
      <form onSubmit={handlePrioritySignup} className="max-w-md mx-auto mb-8">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 h-12"
            data-testid="priority-email-input"
          />
          <Button 
            type="submit" 
            className="bg-secondary hover:bg-secondary-hover text-white h-12 px-6"
            disabled={submitting}
            data-testid="priority-signup-btn"
          >
            {submitting ? 'Joining...' : 'Join Priority List'}
          </Button>
        </div>
      </form>

      {/* Benefits */}
      <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <span>First to know</span>
        </div>
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <Shield className="w-4 h-4 text-green-600" />
          <span>Vetted employers</span>
        </div>
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <FileText className="w-4 h-4 text-green-600" />
          <span>No spam, ever</span>
        </div>
      </div>

      {/* Alternative CTA */}
      <div className="mt-8 pt-8 border-t border-gray-200">
        <p className="text-gray-500 mb-4">Or upload your resume now for faster matching</p>
        <Link to="/register">
          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Upload Resume (SW Express Apply™)
          </Button>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-[#0F2E24] py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-secondary/20 flex items-center justify-center mx-auto mb-6">
              <IconComponent className="w-8 h-8 text-secondary" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {heroTitle || title}
            </h1>
            <p className="text-lg text-gray-200 max-w-3xl mx-auto mb-8">
              {heroSubtitle || `Find ${title.toLowerCase()} opportunities with verified employers across the United States.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/jobs?search=${encodeURIComponent(searchKeywords[0])}`}>
                <Button className="bg-secondary hover:bg-secondary-hover text-white px-8 h-12" data-testid="browse-category-jobs">
                  <Search className="w-4 h-4 mr-2" />
                  {totalJobs > 0 ? `Browse ${totalJobs}+ Jobs` : 'Search Jobs'}
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8 h-12">
                  Create Free Profile
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Status Banner */}
      <section className="bg-white border-b py-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 text-center">
            <Shield className="w-5 h-5 text-primary" />
            <p className="text-gray-600">
              <span className="font-medium text-gray-900">Texas-Based. Nationwide Reach.</span>
              {' '}— Employers in this specialty are currently being vetted.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Content Column */}
            <div className="lg:col-span-2">
              {contentSections && contentSections.map((section, index) => (
                <div key={index} className="mb-10">
                  <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">
                    {section.title}
                  </h2>
                  <div className="prose prose-lg text-gray-700 max-w-none">
                    {section.content}
                  </div>
                  {section.list && (
                    <ul className="mt-4 space-y-2">
                      {section.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {/* CTA Box */}
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mb-10">
                <h3 className="font-semibold text-gray-900 mb-2">Ready to find your next opportunity?</h3>
                <p className="text-gray-600 mb-4">
                  Create a free profile and apply to jobs with our SW Express Apply™ feature.
                </p>
                <Link to="/register">
                  <Button className="bg-primary hover:bg-primary-hover text-white">
                    Get Started Free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Featured Jobs or Coming Soon */}
              <div className="bg-white rounded-xl shadow-sm border p-6 mb-6 sticky top-24">
                <h3 className="font-semibold text-gray-900 mb-4">Featured {title}</h3>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                        <div className="h-3 bg-gray-200 rounded w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : jobs.length > 0 ? (
                  <div className="space-y-4">
                    {jobs.slice(0, 5).map(job => (
                      <Link 
                        key={job.job_id} 
                        to={`/jobs/${job.job_id}`}
                        className="block p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                      >
                        <p className="font-medium text-gray-900 text-sm line-clamp-1">{job.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{job.company_name}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">{job.license_required}</Badge>
                          <span className="text-xs text-gray-500">{job.location?.split(',')[0]}</span>
                        </div>
                      </Link>
                    ))}
                    <Link to={`/jobs?search=${encodeURIComponent(searchKeywords[0])}`}>
                      <Button variant="outline" className="w-full mt-2" size="sm">
                        View All Jobs
                        <ArrowRight className="w-3 h-3 ml-2" />
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Bell className="w-8 h-8 text-secondary mx-auto mb-3" />
                    <p className="text-gray-600 text-sm mb-3">Positions coming soon</p>
                    <form onSubmit={handlePrioritySignup} className="space-y-2">
                      <Input
                        type="email"
                        placeholder="Your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="text-sm"
                      />
                      <Button 
                        type="submit" 
                        size="sm"
                        className="w-full bg-secondary hover:bg-secondary-hover text-white"
                        disabled={submitting}
                      >
                        Notify Me
                      </Button>
                    </form>
                  </div>
                )}
              </div>

              {/* Related Categories */}
              {relatedCategories && relatedCategories.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Related Categories</h3>
                  <div className="space-y-2">
                    {relatedCategories.map((cat, index) => (
                      <Link 
                        key={index}
                        to={cat.href}
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 hover:text-primary"
                      >
                        <ArrowRight className="w-4 h-4" />
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Job Listings Section or Coming Soon */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-serif text-2xl font-bold text-gray-900">Latest {title}</h2>
                  <p className="text-gray-600">Recently posted opportunities</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-gray-50 rounded-lg p-6 animate-pulse">
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
                    <div className="h-3 bg-gray-200 rounded w-full" />
                  </div>
                ))}
              </div>
            </>
          ) : jobs.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-serif text-2xl font-bold text-gray-900">Latest {title}</h2>
                  <p className="text-gray-600">Recently posted opportunities</p>
                </div>
                <Link to={`/jobs?search=${encodeURIComponent(searchKeywords[0])}`}>
                  <Button variant="outline">
                    View All
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {jobs.slice(0, 8).map(job => (
                  <JobCard key={job.job_id} job={job} showActions={false} />
                ))}
              </div>
            </>
          ) : (
            <ComingSoonSection />
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Be First to Know When {title.replace('Jobs', '')} Jobs Go Live
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Join our priority candidate list and get notified when verified employers post new positions. 
            Create your profile now to be ready when opportunities open.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/jobs">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900 px-8 h-12">
                Browse All Jobs
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-secondary hover:bg-secondary-hover text-white px-8 h-12">
                Create Free Account
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobCategoryPage;
